import classNames from 'classnames';
import ChatWidget from 'components/common/chat-widget/ChatWidget';
import Footer from 'components/footers/Footer';
import NavbarDual from 'components/navbars/navbar-dual/NavbarDual';
import NavbarTopHorizontal from 'components/navbars/navbar-horizontal/NavbarTopHorizontal';
import NavbarTopDefault from 'components/navbars/navbar-top/NavbarTopDefault';
import NavbarVertical from 'components/navbars/navbar-vertical/NavbarVertical';
import { useAppContext } from 'providers/AppProvider';
import { useMainLayoutContext } from 'providers/MainLayoutProvider';
import { Container } from 'react-bootstrap';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useEffect, useRef } from 'react';

import { useAppSelector, useAppDispatch } from 'redux/store';
import { fetchUserData } from '../redux/slices/authThunk'; // Adjust the import path for your fetchUserData action
import { useTranslation } from 'react-i18next';
import 'reactflow/dist/style.css';
import 'bootstrap/dist/css/bootstrap.min.css';

const MainLayout = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { i18n } = useTranslation();
  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };
  const userLanguage = useAppSelector((state) => state.auth.userData?.language || 'nl'); // Default to 'nl' if no language is set
  useEffect(() => {
    // Only change the language if it differs from the i18n state
    if (userLanguage && i18n.language !== userLanguage) {
      console.log('changeLanguage to ' + userLanguage);
      i18n.changeLanguage(userLanguage); // Automatically set the language
    }
  }, [userLanguage, i18n]);

  const {
    config: { navbarPosition },
  } = useAppContext();

  const { contentClass, footerClass } = useMainLayoutContext();

  const token = useAppSelector((state) => state.auth.token); // Redux auth token
  const isAuthenticated = token != null; // Assuming token existence means authenticated
  const userLoading = useAppSelector((state) => state.auth.loading); // Redux loading state
  const userData = useAppSelector((state) => state.auth.userData);

  // Track whether the initial user data load has completed
  const hasLoadedOnce = useRef(false);

  // Mark initial load as complete once we have user data
  useEffect(() => {
    if (userData && !userLoading) {
      hasLoadedOnce.current = true;
    }
  }, [userData, userLoading]);

  // Fetch user data if token is available, this runs as soon as MainLayout component is mounted
  useEffect(() => {
    if (isAuthenticated) {
      console.log('MainLayout.tsx useEffect - isAuthenticated');
      dispatch(fetchUserData()); // Dispatch to fetch user data if authenticated
    }
  }, [isAuthenticated, dispatch]);

  // Only show the loading screen on the initial load when authenticated
  const isInitialLoading = isAuthenticated && !hasLoadedOnce.current && (userLoading || !userData);

  // If still loading, show a loading spinner or message
  if (isInitialLoading) {
    console.log('MainLayout.tsx - isLoading (initial)');
    return <div>Loading...</div>; // You can replace this with a spinner component
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    console.log('MainLayout.tsx - !isAuthenticated');
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />; // Redirect to login if not authenticated
  }

  console.log('MainLayout.tsx - end of all checks before last return');

  const hasVerticalNav = navbarPosition === 'vertical' || navbarPosition === 'combo';

  return (
    <Container fluid className="px-0 app-frame">
      {navbarPosition === 'vertical' && <NavbarTopDefault />}
      {(navbarPosition === 'horizontal' || navbarPosition === 'combo') && <NavbarTopHorizontal />}
      {navbarPosition === 'dual' && <NavbarDual />}

      <div className="app-body">
        {hasVerticalNav && <NavbarVertical loggedIn={isAuthenticated} />}

        <div className="app-content-col">
          <div className={classNames(contentClass, 'content')}>
            <Outlet />
          </div>
          <Footer className={footerClass} />
        </div>
      </div>

      <ChatWidget />
    </Container>
  );
};

export default MainLayout;
